import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseWhite, IconShareChannel, IconShareInvite } from '../../../assets/images';
import {
   clearChannelPartcipants,
   getAppOnlineStatus,
   removeParticipant,
   selectParticipant,
   useChanneInfoActionPending,
   useLeftPaneData,
   useParticipantsSelected,
} from '../../../Slice';
import { AddOrSkipButton, SearchBox, UserProfileImage } from '../../components';
import ChannelSharePopup from '../../conversation/ChannelMessage/components/ChannelSharePopup';
import { EmptySearch, loaderGIF, NO_RESULTS_FOUND } from '../../rootAccess';
import {
   handleAddMembersToChannel,
   handleChannelNavigation,
   handleInviteMembers,
   resetActionModal,
   setActionModal,
   useBidirectionalInfiniteScroll,
   usePaginatedUserList,
} from '../../utils';
import './UserList.scss';

const UserListItem = memo(({ user, userListScopeKey }) => {
   const dispatch = useDispatch();
   const participants = useSelector(state => state.channelInfoData?.userListSelection?.[userListScopeKey]);
   const isChecked = participants?.some(p => p.userId === user.userId) || false;

   const handleChange = useCallback(
      e => {
         const checked = e.target.checked;
         const action = checked ? selectParticipant : removeParticipant;
         dispatch(action({ userListScopeKey, userId: user.userId }));
      },
      [dispatch, userListScopeKey, user.userId],
   );

   return (
      <div className="channel-user-list-item" key={user.userId}>
         <div className="channel-user-image">
            <UserProfileImage userId={user.userId} />
         </div>
         <div className="channel-user-details">
            <h3 className="channel-user-name">{user.nickName || user.userId}</h3>
            <p className="channel-user-status">{user.status || 'No status'}</p>
         </div>
         <div className="channel-user-action">
            <input
               type="checkbox"
               className="channel-user-checkbox"
               id={`${userListScopeKey}-user-checkbox-${user.userId}`}
               checked={isChecked}
               onChange={handleChange}
            />
            <label
               htmlFor={`${userListScopeKey}-user-checkbox-${user.userId}`}
               className="checkbox-label"
               aria-label="checkbox-label"
            />
         </div>
      </div>
   );
});

UserListItem.displayName = 'UserListItem';

UserListItem.propTypes = {
   user: PropTypes.object.isRequired,
   userListScopeKey: PropTypes.string,
};

const EmptyState = memo(({ isOnline }) => (
   <div className="empty-view">
      <div className="norecent-chat">
         {isOnline ? (
            <>
               <i className="norecent-chat-img">{<EmptySearch />}</i>
               <h4>{NO_RESULTS_FOUND}</h4>
            </>
         ) : (
            <h4>No internet connection</h4>
         )}
      </div>
   </div>
));

EmptyState.displayName = 'EmptyState';
EmptyState.propTypes = {
   isOnline: PropTypes.bool.isRequired,
};

const LoadingIndicator = memo(() => (
   <div style={{ textAlign: 'center', padding: '10px' }}>
      <img src={loaderGIF} alt="Loading more users" style={{ width: '40px', height: '40px' }} />
   </div>
));

LoadingIndicator.displayName = 'LoadingIndicator';

const UserListContent = memo(({ userList, userListScopeKey, isOnline }) => (
   <>
      {isOnline &&
         userList.map(user => <UserListItem key={user.userId} user={user} userListScopeKey={userListScopeKey} />)}
   </>
));

UserListContent.displayName = 'UserListContent';
UserListContent.propTypes = {
   userList: PropTypes.array.isRequired,
   userListScopeKey: PropTypes.string,
   isOnline: PropTypes.bool.isRequired,
};

function UsersList({
   isInModal = false,
   userListScopeKey = 'page',
   mode = 'add', // 'add' | 'invite'
   onPrimaryAction,
   primaryActionLabel,
   showInviteButton = true,
   showShareButton = false,
}) {
   const leftPaneData = useLeftPaneData();
   const dispatch = useDispatch();
   const { params: { channelId } = {} } = leftPaneData;
   const isPartcipantsSelected = useParticipantsSelected(userListScopeKey);
   const scrollRef = useRef(null);
   const isActionPending = useChanneInfoActionPending();
   const { userList, loading = true, hasMore, loadMore, setSearchText, reset } = usePaginatedUserList({ channelId });
   const { loadingBottom } = useBidirectionalInfiniteScroll({
      containerRef: scrollRef,
      onLoadTop: null,
      onLoadBottom: loadMore,
      hasMoreTop: false,
      hasMoreBottom: hasMore,
   });
   const isOnline = useSelector(getAppOnlineStatus);
   const prevIsOnline = useRef(isOnline);
   const isInviteMode = useRef(false);

   useEffect(() => {
      if (!prevIsOnline.current && isOnline) {
         reset();
      }
      prevIsOnline.current = isOnline;
   }, [isOnline, reset]);

   useEffect(() => {
      return () => {
         if (!isInviteMode.current) {
            dispatch(clearChannelPartcipants({ userListScopeKey }));
         }
         isInviteMode.current = false;
      };
   }, [dispatch, userListScopeKey]);

   // Specialized action handlers
   const handleAddMembersAction = useCallback(() => {
      handleAddMembersToChannel(channelId, userListScopeKey);
   }, [channelId, userListScopeKey]);

   const handleInviteMembersAction = useCallback(() => {
      handleInviteMembers(channelId, userListScopeKey);
   }, [channelId, userListScopeKey]);

   const handleShareChannelAction = useCallback(() => {
      setActionModal({
         visible: true,
         body: <ChannelSharePopup channelId={channelId} onClose={() => resetActionModal()} />,
         onRequestClose: () => resetActionModal(),
      });
   }, [channelId]);

  const handleNavigateToInvite = useCallback(() => {
      isInviteMode.current = true;
      setActionModal({
         visible: true,
         header: 'Invite Members on channel',
         headerIconLeft: <CloseWhite />,
         body: (
            <UsersList
               isInModal={true}
               userListScopeKey={userListScopeKey}
               mode="invite"
               showInviteButton={false}
               showShareButton={true}
               onPrimaryAction={handleInviteMembersAction}     
               primaryActionLabel="Invite Members"             
            />
         ),
         onRequestClose: () => {
            isInviteMode.current = false;
            resetActionModal();
         },
      });
   }, [userListScopeKey, handleInviteMembersAction]);

   const handleSkipAction = useCallback(() => {
      handleChannelNavigation({ pane: 'recentChannelList' });
   }, []);

   // Determine primary action based on props or mode
   const getPrimaryAction = useCallback(() => {
      if (onPrimaryAction) {
         return onPrimaryAction;
      }
      return mode === 'invite' ? handleInviteMembersAction : handleAddMembersAction;
   }, [onPrimaryAction, mode, handleInviteMembersAction, handleAddMembersAction]);

   const getPrimaryActionLabel = useCallback(() => {
      if (primaryActionLabel) {
         return primaryActionLabel;
      }
      return mode === 'invite' ? 'Invite Members' : 'Continue';
   }, [primaryActionLabel, mode]);

   const renderUserListSection = useMemo(() => {
      const shouldShowEmpty = !isOnline || (!loading && userList.length === 0);
      const shouldShowLoading = loadingBottom || loading;

      return (
         <>
            {isInModal && <SearchBox onChange={setSearchText} autoFocus={true} />}
            {!isInModal && (
               <div className="add-members-header">
                  <SearchBox onChange={setSearchText} autoFocus={true} />
                  {showInviteButton && (
                     <button className="invite-link-button" onClick={handleNavigateToInvite}>
                        <i className="icon">
                           <IconShareInvite />
                        </i>
                        Send invite
                     </button>
                  )}
               </div>
            )}
            {/* {showShareButton && (
               <button className="share-link-button" onClick={handleShareChannelAction}>
                  <IconShareChannel />
                  Share Link
               </button>
            )} */}
            <div
               ref={scrollRef}
               id="scrollableDiv"
               className={`channel-user-list chat-list ${isInModal ? 'modal-scroll' : ''} ${
                  mode === 'invite' ? 'invite-scroll' : ''
               }`}
               style={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
               }}>
               {shouldShowEmpty && <EmptyState isOnline={isOnline} />}
               <UserListContent userList={userList} userListScopeKey={userListScopeKey} isOnline={isOnline} />
               {/* {shouldShowLoading && <LoadingIndicator />} */}
            </div>
         </>
      );
   }, [
      userList,
      loadingBottom,
      loading,
      isOnline,
      userListScopeKey,
      mode,
      isInModal,
      setSearchText,
      showInviteButton,
      showShareButton,
      handleNavigateToInvite,
      handleShareChannelAction,
   ]);

   return (
      <div style={{ position: 'relative' }}>
         {renderUserListSection}
         <AddOrSkipButton
            value={isPartcipantsSelected}
            option1={'Skip'}
            option2={getPrimaryActionLabel()}
            option1Action={handleSkipAction}
            option2Action={getPrimaryAction()}
            hideOption1={isInModal}
         />
         {/* Full-page overlay loader */}
         {isActionPending && (
            <div
               style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
               }}>
               <img src={loaderGIF} alt="Loading..." style={{ width: '50px', height: '50px' }} />
            </div>
         )}
      </div>
   );
}

/**
 * Refactored UsersList component with multiple specialized methods instead of isInvite conditionals
 *
 * Usage Examples:
 *
 * // Method 1: Using mode prop
 * <UsersList mode="add" showInviteButton={true} />
 * <UsersList mode="invite" showShareButton={true} />
 *
 * // Method 2: Using convenience wrappers
 * <AddMembersUsersList />
 * <InviteMembersUsersList />
 *
 * // Method 3: Custom action handlers
 * <UsersList
 *   onPrimaryAction={customHandler}
 *   primaryActionLabel="Custom Action"
 * />
 *
 * // Method 4: Backwards compatibility (deprecated)
 * <LegacyUsersList isInvite={true} />
 */

// Convenience wrapper components for common use cases
export const AddMembersUsersList = props => (
   <UsersList {...props} mode="add" showInviteButton={true} showShareButton={false} />
);

export const InviteMembersUsersList = props => (
   <UsersList {...props} mode="invite" showInviteButton={false} showShareButton={true} />
);

// For backwards compatibility
export const LegacyUsersList = ({ isInvite, ...props }) => (
   <UsersList {...props} mode={isInvite ? 'invite' : 'add'} showInviteButton={!isInvite} showShareButton={isInvite} />
);

LegacyUsersList.propTypes = {
   isInvite: PropTypes.bool,
};

export default UsersList;

UsersList.propTypes = {
   isInModal: PropTypes.bool,
   userListScopeKey: PropTypes.string,
   mode: PropTypes.oneOf(['add', 'invite']),
   onPrimaryAction: PropTypes.func,
   primaryActionLabel: PropTypes.string,
   showInviteButton: PropTypes.bool,
   showShareButton: PropTypes.bool,
};
